import { Component, inject, Inject } from '@angular/core';
import { SharedModule } from '../../../../../shared/shared.module';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { IContactWidget, IManualContactDeleteData } from '../../../../interfaces/contact-widget.interface';
import { ContactWidgetService } from '../../../../services/contact-widget.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ContactGroup, ContactReference, ContactSource, ManualContact } from '../../../../interfaces/contact.interface';
import { ContactUtils } from '../../../../utils/contact-utils';
import { TranslateService } from '@ngx-translate/core'

@Component({
  selector: 'lib-manual-contact-delete',
  imports: [SharedModule],
  templateUrl: './manual-contact-delete.component.html',
  styleUrl: './manual-contact-delete.component.scss'
})
export class ManualContactDeleteComponent {
  // Services
  private readonly contactWidgetService: ContactWidgetService = inject(ContactWidgetService);
  private readonly snackBar: MatSnackBar = inject(MatSnackBar);
  private readonly translate: TranslateService = inject(TranslateService);

  protected readonly ContactUtils = ContactUtils
  contactWidget: IContactWidget;

  constructor(
      @Inject(MAT_DIALOG_DATA) public data: IManualContactDeleteData,
      private readonly dialogRef: MatDialogRef<ManualContactDeleteComponent>
  ) {
    this.contactWidget = data.widget;
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  async onDelete(): Promise<void> {
    if (!this.contactWidget?._id) {
      console.error('[ManualContactDelete] Cannot delete - widget ID missing');
      this.snackBar.open(this.translate.instant('ASTIMAX.CONTACT.WIDGET.FAILED_TO_DELETE_CONTACT'),
          'Close', { duration: 3000 }
      );
      return;
    }

    try {
      // Delete the manual contact
      const deleteSuccess = await this.deleteManualContactFromDB(this.data.contact);

      // Remove contact from all groups that contain it
      await this.removeContactFromAllGroups(this.data.contact);

      // Save widget settings to database
      await this.contactWidgetService.saveContactWidgetSettingsToDatabase(
          this.contactWidget._id,
          this.contactWidget
      );

      // Close dialog with success result
      this.dialogRef.close({
	      contactId: this.data.contact.contactId,
        name: this.data.contact.name,
        confirmed: true,
        widget: this.contactWidget,
        success: deleteSuccess
      });
    } catch (error) {
      console.error('[ManualContactDelete] Error deleting contact:', error);
      this.snackBar.open(this.translate.instant('ASTIMAX.CONTACT.WIDGET.FAILED_TO_DELETE_CONTACT'),
          'Close',
          { duration: 3000 }
      );
    }
  }

  private async deleteManualContactFromDB(contactToDelete: ManualContact): Promise<boolean> {
    if (!this.contactWidget?._id) {
      throw new Error('Widget ID missing');
    }

    // Get current manual contacts from service
    const widgetData = this.contactWidgetService.getWidgetData(this.contactWidget._id);
    if (!widgetData) {
      throw new Error('Widget data not found');
    }

    const currentManualContacts = widgetData.manualContacts;
    const updatedManualContacts = currentManualContacts.filter(
        (contact: ManualContact) => contact.contactId !== contactToDelete.contactId
    );

    // Update manual contacts
    this.contactWidgetService.updateManualContacts(this.contactWidget._id, updatedManualContacts, true);

	  // Delete the manual contact from the database
	  return await this.contactWidgetService.deleteAstimaxContactManualContact(contactToDelete);
  }

  private async removeContactFromAllGroups(contactToDelete: ManualContact): Promise<void> {
    if (!this.contactWidget?._id) {
      throw new Error('Widget ID missing');
    }

    // Get current groups from service
    const widgetData = this.contactWidgetService.getWidgetData(this.contactWidget._id);
    if (!widgetData) {
      throw new Error('Widget data not found');
    }

    const currentGroups = widgetData.contactGroups;

    // Remove the contact from all groups
    const updatedGroups = currentGroups.map((group: ContactGroup) => ({
      ...group,
      contacts: group.contacts.filter((ref: ContactReference) =>
          !(ref.contactId === contactToDelete.contactId && ref.source === ContactSource.MANUAL)
      ),
      updated: new Date()
    }));

    // Update contact groups through service
    this.contactWidgetService.updateContactGroups(this.contactWidget._id, updatedGroups);

    // Update widget settings
    if (this.contactWidget.settings) {
      this.contactWidget.settings.contactGroups = updatedGroups;
    }
  }

  canDelete(): boolean {
    return true;
  }
}
